/**
 * DuviNicheSiteIcon — one Lucide glyph rendered inline so it inherits
 * `currentColor`. Ported from the bespoke Fishwalkers design system's
 * `components/core/Icon.jsx`
 * (duvi-design/exports/niches/fishwalkers/system.zip).
 *
 * The export embeds each glyph's path data because a file-URL CSS mask does
 * not resolve reliably in every host — the markup has to travel with the
 * component. It does that with `dangerouslySetInnerHTML` over an HTML
 * string; this port keeps the identical rendered SVG but expresses the paths
 * as JSX, so no `dangerouslySetInnerHTML` sits in a production bundle
 * (constitution V). Path data is copied verbatim from
 * `system.zip:assets/icons/*.svg`.
 *
 * Only the glyphs the brochure actually composes are carried across. The set
 * is Lucide at 2px stroke with rounded caps — never hand-draw a substitute,
 * and never hard-code a stroke colour.
 *
 * Decorative by default: without a `label` the glyph is `aria-hidden`. Pass
 * `label` only when the icon carries meaning no adjacent text carries.
 */
export type DuviNicheSiteIconName = "house" | "footprints" | "map-pin";

export interface DuviNicheSiteIconProps {
  name: DuviNicheSiteIconName;
  /** 20px inline with text, 22px inside a 44px accent badge, 26px maximum. */
  size?: number;
  /** Accessible name. Omit for a decorative glyph (the default). */
  label?: string;
  className?: string;
}

function DuviNicheSiteIconGlyph({ name }: { name: DuviNicheSiteIconName }) {
  if (name === "footprints") {
    return (
      <>
        <path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z" />
        <path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z" />
        <path d="M16 17h4" />
        <path d="M4 13h4" />
      </>
    );
  }
  if (name === "map-pin") {
    return (
      <>
        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
        <circle cx="12" cy="10" r="3" />
      </>
    );
  }
  return (
    <>
      <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
      <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </>
  );
}

export function DuviNicheSiteIcon({
  name,
  size = 20,
  label,
  className = "",
}: DuviNicheSiteIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : "true"}
      className={["inline-block shrink-0 align-middle", className].join(" ")}
    >
      <DuviNicheSiteIconGlyph name={name} />
    </svg>
  );
}
